import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import PageHeaderText from 'components/base/page-header-text';
import FeedCard from 'components/feed-card';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { BaseAddonCategoryType } from 'utils/types';
import { ReactComponent as EditIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import DeleteModal from 'components/base/delete-modal';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import { getBaseAddonCategory } from '../util';
import SearchNav from 'components/search-nav';
import useDashboardContext from 'contexts/dashboard.context';
import EditBulkBaseAddonCategory from './edit-bulk-base-addon-category';
import AccessDenied from 'components/access-denied';

const BaseAddonCategory = () => {
  const navigate = useNavigate();
  const [baseAddonCategories, setBaseAddonCategories] = useState<BaseAddonCategoryType[]>([]);
  const [active, setActive] = useState({} as BaseAddonCategoryType);
  const [loading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBaseAddonCat, setSelectedBaseAddonCat] = useState<string[]>([]);
  const [toggleBulkEditModal, setToggleBulkEditModal] = useState(false);
  const { customSearch, setIsCustomSearch, permissions } = useDashboardContext();

  const addViaXls = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.BASE_ADDON_CATEGORY_XLS}`);
  };
  useEffect(() => {
    setIsCustomSearch(true);
    return () => setIsCustomSearch(false);
  }, []);
  useEffect(() => {
    if (customSearch) return;
    fetchBaseAddonCategories();
  }, [customSearch]);
  useEffect(() => {
    if (!customSearch) return;
    const getCats = setTimeout(() => {
      onSearch();
    }, 1000);

    return () => {
      clearTimeout(getCats);
    };
  }, [customSearch]);
  const onSearch = async () => {
    try {
      const res = await apiService.get('base-addon-category/search/names', {
        params: { search: customSearch }
      });
      if (res?.data) {
        setBaseAddonCategories(res?.data?.data);
      }
    } catch {
      toast(<ToastMessage type='error' message={'Error searching base addon category'} />);
    }
  };
  const handleSelectAll = () => {
    setSelectedBaseAddonCat(baseAddonCategories.map((item) => item.id));
  };
  const fetchBaseAddonCategories = async () => {
    const categories = await getBaseAddonCategory();
    setBaseAddonCategories(categories);
  };
  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };
  const handleChange = (id: string) => {
    if (selectedBaseAddonCat?.includes(id)) {
      setSelectedBaseAddonCat(selectedBaseAddonCat?.filter((item) => item !== id));
    } else {
      setSelectedBaseAddonCat([...selectedBaseAddonCat, id]);
    }
  };

  const handleDeleteClick = async () => {
    try {
      const res: any = await apiService.post(`base-addon-category/bulk/update`, {
        data: {
          base_addon_category_ids: selectedBaseAddonCat ? selectedBaseAddonCat : [active.id],
          archived: true
        },
        isInternal: true
      });
      if (res?.data) {
        fetchBaseAddonCategories();
      }
    } catch {
      toast(<ToastMessage type='error' message={'Error deleting base addon category'} />);
    }
  };

  if (!permissions.canAccessBaseItems) return <AccessDenied />;
  return (
    <div>
      <PageHeaderText title='Base Addon Category '>
        <div className='flex items-center gap-5 '>
          <Button
            color='primary'
            variant='link'
            className='cursor-pointer text-sm font-medium text-[#E60A2B] w-fit'
            onClick={() => navigate(`${ROUTE_PREFIX.FOOD}/create-base-addon-category`)}>
            + Add new
          </Button>
          <Button small className='' variant='block' color='primary' onClick={addViaXls}>
            Add via xls
          </Button>
        </div>
      </PageHeaderText>
      <div>
        {loading ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>No base addon category available</p>
          </div>
        ) : baseAddonCategories?.length === 0 ? (
          <div className='w-full mt-10 flex justify-center items-center'>
            <p className='font-medium text-lg'>No base addon category available</p>
          </div>
        ) : (
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
            {baseAddonCategories?.map((baseAddonCategory) => (
              <div key={baseAddonCategory.id}>
                <FeedCard
                  className='mb-2'
                  onSelect={() => handleChange(baseAddonCategory.id)}
                  showCheckBox
                  checked={selectedBaseAddonCat.includes(baseAddonCategory.id)}
                  actions={
                    <div className='flex gap-2 absolute top-2 right-2'>
                      <ArchiveIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteModal(true);
                          setActive(baseAddonCategory);
                        }}
                        className={` w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10`}
                      />
                      <EditIcon
                        onClick={() =>
                          navigate(
                            `${ROUTE_PREFIX.FOOD}/edit-base-addon-category/${baseAddonCategory.id}`
                          )
                        }
                        className=' w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10 edit_icon'
                      />
                    </div>
                  }>
                  <div className=''>
                    <p>{baseAddonCategory?.slug}</p>
                    <p className='text-sm'>{baseAddonCategory?.name}</p>
                  </div>
                </FeedCard>
              </div>
            ))}
          </div>
        )}
      </div>
      <EditBulkBaseAddonCategory
        setSelectedBaseAddonCat={setSelectedBaseAddonCat}
        refreshData={getBaseAddonCategory}
        show={toggleBulkEditModal}
        onClose={() => setToggleBulkEditModal(false)}
        data={selectedBaseAddonCat}
      />
      <DeleteModal
        show={showDeleteModal}
        onClose={toggleDeleteModal}
        handleDelete={handleDeleteClick}
        title={`Are you sure you want to delete ${active?.name ?? 'categories'} ?`}
        description='This action cannot be undone'
        loading={loading}
      />
      {selectedBaseAddonCat?.length > 0 && (
        <SearchNav
          count={selectedBaseAddonCat.length}
          handleSelectAll={handleSelectAll}
          onDoneClick={() => setSelectedBaseAddonCat([])}
          onEditClick={() => setToggleBulkEditModal(true)}
          onDeleteClick={() => setShowDeleteModal(true)}
        />
      )}
    </div>
  );
};

export default BaseAddonCategory;
